import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { experience, education } from '../data/portfolio';

export default function Experience() {
  const [ref, inView] = useInView(0.1);

  const items = [
    ...experience.map(e => ({ ...e, _type: 'work' })),
    ...education.map(e => ({ role: e.degree, company: e.institution, period: e.period, location: e.location, highlights: [], type: 'Education', color: '#3b82f6', _type: 'edu' })),
  ];

  return (
    <section id="experience" className="section" style={{ background: 'linear-gradient(to bottom, var(--bg), var(--bg2))' }}>
      <div style={{ position: 'absolute', top: 0, left: '5%', right: '5%', height: 1, background: 'linear-gradient(to right, transparent, rgba(0,245,255,0.08), transparent)' }} />
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '3.5rem' }}
        >
          <div className="sec-tag">04 · Experience</div>
          <h2 className="sec-title">
            Where I've{' '}
            <span style={{ background: 'linear-gradient(90deg, var(--cyan), var(--teal))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              worked
            </span>
          </h2>
        </motion.div>

        <div style={{ position: 'relative', paddingLeft: 'clamp(1.5rem, 5vw, 3rem)' }}>
          {/* Glowing vertical line */}
          <motion.div
            initial={{ scaleY: 0 }}
            animate={inView ? { scaleY: 1 } : {}}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: 1,
              background: 'linear-gradient(to bottom, var(--cyan), rgba(0,245,255,0.2), transparent)',
              boxShadow: '0 0 8px var(--cyan)',
              transformOrigin: 'top',
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -40 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.75, delay: 0.3 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                style={{ position: 'relative' }}
              >
                {/* Dot + pulse */}
                <div style={{ position: 'absolute', left: 'clamp(-1.85rem, -5.5vw, -3.35rem)', top: 10, zIndex: 1 }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%',
                    background: item.color,
                    border: '2px solid var(--bg)',
                    boxShadow: `0 0 16px ${item.color}, 0 0 30px ${item.color}50`,
                    position: 'relative',
                  }}>
                    <motion.div
                      animate={{ scale: [1, 2, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.5 }}
                      style={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        background: item.color, opacity: 0.4,
                      }}
                    />
                  </div>
                </div>

                {/* Card */}
                <div
                  className="glass-card"
                  style={{ padding: 'clamp(1.25rem, 3vw, 1.75rem)', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = item.color + '40'; e.currentTarget.style.boxShadow = `0 10px 40px ${item.color}15`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,245,255,0.1)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {/* Top accent bar */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(to right, ${item.color}60, transparent)`, borderRadius: '18px 18px 0 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: item.highlights.length > 0 ? '1rem' : 0 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                        <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', color: 'var(--white)' }}>
                          {item.role}
                        </h3>
                        {item.type && (
                          <span style={{
                            fontFamily: 'var(--font-display)', fontSize: '0.52rem', letterSpacing: '0.1em',
                            padding: '0.15rem 0.55rem',
                            background: item.color + '18',
                            border: `1px solid ${item.color}35`,
                            borderRadius: '4px',
                            color: item.color,
                            textTransform: 'uppercase',
                          }}>
                            {item.type}
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.7rem', letterSpacing: '0.05em', color: item.color, textShadow: `0 0 10px ${item.color}60` }}>
                        {item.company}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.62rem', color: 'var(--grey)', letterSpacing: '0.04em' }}>{item.period}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--grey2)', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.3rem', justifyContent: 'flex-end' }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {item.location}
                      </div>
                    </div>
                  </div>

                  {item.highlights.length > 0 && (
                    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      {item.highlights.map((h, j) => (
                        <li key={j} style={{ display: 'flex', gap: '0.6rem', fontSize: '0.83rem', color: 'var(--grey)', lineHeight: 1.6, alignItems: 'flex-start' }}>
                          <span style={{ color: item.color, flexShrink: 0, marginTop: '0.3rem', fontSize: '0.5rem', textShadow: `0 0 6px ${item.color}` }}>◆</span>
                          {h}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
